<template>
  <div>
    <div class="captcha-container">
      <!-- 验证码输入区域 -->
      <div class="input-group">
        <div class="captcha-inputs">
          <input
            v-for="index in codeLength"
            :key="index"
            ref="codeInputs"
            v-model="codeValues[index-1]"
            type="text"
            maxlength="1"
            inputmode="numeric"
            pattern="\d*"
            :disabled="isFirst"
            @input="handleInput($event, index-1)"
            @keydown="handleKeyDown($event, index-1)"
            @focus="handleFocus(index-1)"
            :class="{ 'focused': focusedIndex === index-1 }"
          >
        </div>
      </div>
    </div>
      <!-- 获取验证码按钮 -->
    <div class="action-group">
      <van-button
        class="get-captcha-btn"
        :class="{ 'disabled': isCountingDown }"
        :disabled="isCountingDown || !isPhoneValid"
        @click="handleGetCaptcha"
        size="small"
        type="primary"
      >
        <span v-if="!isCountingDown" style="">获取验证码</span>
        <span v-else>重新发送({{ countdown }}s)</span>
      </van-button>
    </div>
    <!-- 点击验证组件 -->
    <click-verification 
      ref="clickVerification" 
      @verification-success="onVerificationSuccess"
    />
  </div>
</template>

<script>
import cache from '@/utils/cacheUtils';
import ClickVerification from './ClickVerification.vue';

export default {
  name: 'CaptchaInput',
  components: {
    ClickVerification
  },
  props: {
    phoneNumber: {
      type: String,
      default: ''
    },
    isPhoneValid: {
      type: Boolean,
      default: true
    },
    codeLength: {
      type: Number,
      default: 6
    },
    // 新增：倒计时时长配置
    countdownDuration: {
      type: Number,
      default: 60
    }
  },
  data() {
    return {
      codeValues: [],
      isFirst: true,
      focusedIndex: 0,
      isCountingDown: false,
      countdown: 0,
      timer: null,
      endTimestamp: 0 // 新增：倒计时结束的时间戳
    };
  },
  created() {
    this.codeValues = new Array(this.codeLength).fill('');
    // 组件创建时初始化倒计时状态
    this.initCountdown();
  },
  methods: {
    /**
     * 初始化倒计时状态（页面加载时调用）
     */
    initCountdown() {
      // 从缓存中读取之前保存的结束时间戳
      const savedEndTime = cache.get('captchaEndTime');
      const currentTime = Date.now();

      if (savedEndTime && savedEndTime > currentTime) {
        // 计算剩余时间（秒）
        const remainingTime = Math.ceil((savedEndTime - currentTime) / 1000);
        
        if (remainingTime > 0) {
          this.endTimestamp = savedEndTime;
          this.countdown = remainingTime;
          this.isCountingDown = true;
          this.startCountdown();
          console.log('恢复倒计时，剩余时间:', remainingTime + 's');
        } else {
          // 倒计时已过期，清理缓存
          this.clearCountdownCache();
        }
      } else if (savedEndTime && savedEndTime <= currentTime) {
        // 倒计时已结束，清理缓存
        this.clearCountdownCache();
      }
    },

    /**
     * 处理获取验证码按钮点击
     */
    handleGetCaptcha() {
      if (this.isCountingDown) return;
      
      if (!this.isPhoneValid) {
        this.$toast('请输入正确的手机号');
        return;
      }
      
      // 显示点击验证组件
      this.$refs.clickVerification.show();
    },
    
    /**
     * 点击验证成功后的回调
     */
    onVerificationSuccess() {
      this.isFirst = false;
      // 触发获取验证码事件
      this.$emit('get-captcha', this.phoneNumber);

      // 设置倒计时结束时间戳（当前时间 + 倒计时时长）
      this.endTimestamp = Date.now() + this.countdownDuration * 1000;
      
      // 保存到缓存
      cache.set('captchaEndTime', this.endTimestamp);
      
      this.countdown = this.countdownDuration;
      this.isCountingDown = true;
      this.startCountdown();
      
      this.$toast('验证码发送成功');
    },

    /**
     * 开始倒计时（基于时间戳）
     */
    startCountdown() {
      if (this.timer) {
        clearInterval(this.timer);
      }

      this.timer = setInterval(() => {
        const currentTime = Date.now();
        const remainingTime = Math.ceil((this.endTimestamp - currentTime) / 1000);
        
        if (remainingTime > 0) {
          this.countdown = remainingTime;
          // 可选：每隔一段时间更新一次缓存，防止极端情况下的数据丢失
          if (remainingTime % 10 === 0) { // 每10秒更新一次缓存
            cache.set('captchaEndTime', this.endTimestamp);
          }
        } else {
          // 倒计时结束
          this.stopCountdown();
          this.clearCountdownCache();
          this.$emit('countdown-end');
        }
      }, 1000);
    },

    /**
     * 停止倒计时
     */
    stopCountdown() {
      this.isCountingDown = false;
      this.countdown = 0;
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },

    /**
     * 清理倒计时缓存
     */
    clearCountdownCache() {
      cache.remove('captchaEndTime');
    },

    /**
     * 手动重置倒计时（供父组件调用）
     */
    resetCountdown() {
      this.stopCountdown();
      this.clearCountdownCache();
    },

    // 以下方法保持不变
    handleInput(event, index) {
      const value = event.target.value;
      
      if (/^\d$/.test(value)) {
        this.codeValues[index] = value;
        
        if (index < this.codeLength - 1) {
          this.$nextTick(() => {
            this.focusInput(index + 1);
          });
        } else {
          this.$emit('input-complete', this.codeValues.join(''));
        }
      } else {
        this.codeValues[index] = '';
        event.target.value = '';
      }
      
      this.$emit('input-change', this.codeValues.join(''));
    },

    handleKeyDown(event, index) {
      if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault();
        
        if (this.codeValues[index] === '' && index > 0) {
          this.codeValues[index - 1] = '';
          this.$nextTick(() => {
            this.focusInput(index - 1);
          });
        } else {
          this.codeValues[index] = '';
        }
        
        this.$emit('input-change', this.codeValues.join(''));
      }
    },

    handleFocus(index) {
      this.focusedIndex = index;
    },

    focusInput(index) {
      if (this.$refs.codeInputs && this.$refs.codeInputs[index]) {
        this.$refs.codeInputs[index].focus();
        this.focusedIndex = index;
      }
    },

    clearInputs() {
      this.codeValues = new Array(this.codeLength).fill('');
      this.focusInput(0);
      this.$emit('input-change', '');
    }
  },
  watch: {
    codeValues: {
      deep: true,
      handler(newVal) {
        const fullCode = newVal.join('');
        if (fullCode.length === this.codeLength) {
          this.$emit('input-complete', fullCode);
        }
      }
    }
  },
  beforeDestroy() {
    this.stopCountdown();
    // 注意：这里不清理缓存，以便页面刷新后能恢复状态
  }
};
</script>

<style scoped>
/* 样式保持不变 */
.captcha-container {
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.input-group {
  flex: 1;
}

.input-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: #323233;
}

.captcha-inputs {
  display: flex;
  gap: 8px;
}

.captcha-inputs input {
  width: 100%;
  height: 44px;
  text-align: center;
  font-size: 16px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  outline: none;
  transition: all 0.2s;
}

.captcha-inputs input:focus {
  border-color: #1989fa;
  box-shadow: 0 0 0 2px rgba(25, 137, 250, 0.1);
}

.captcha-inputs input.focused {
  border-color: #1989fa;
}

.action-group {
  flex-shrink: 0;
  margin-top: 8px;
  margin-bottom: 8px;
  text-align: left;
}

.get-captcha-btn {
  min-width: 100px;
}

.get-captcha-btn.disabled {
  background-color: #c8c9cc;
  border-color: #c8c9cc;
}
</style>